import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { discountAPI, accountingAPI, salePaymentsAPI, complaintAPI, marketingAPI, emailAPI } from '../utils/api';
import { TenantLogo, useBranding } from '../context/BrandingContext';
import { applyVerticalToMenu } from '../utils/vertical';
import { applyGrowthEngineGate } from '../utils/growthEngineGate';   // Growth AA
import { applyPermissionsToMenu } from '../utils/navPermissions';
import { usePermissions } from '../context/PermissionsContext';
import NotificationBell from './NotificationBell';
import LanguageSelector from './LanguageSelector';
import ProfileMenu from './ProfileMenu';
import AttendanceHeaderButton from './AttendanceHeaderButton';
import AvailabilityToggle from './AvailabilityToggle';
import { MoneyIcon } from './Money';
import {
  Compass,
  LayoutDashboard,
  Users,
  Briefcase,
  FolderOpen,
  Settings,
  FileText,
  BarChart3,
  HandCoins,
  Building2,
  AlertCircle,
  Mail,
  ClipboardList,
  Handshake,
  Library,
  Inbox,
  AtSign,
  Send,
  BrainCircuit,
  FileEdit,
  PenLine,
  Calculator,
  DollarSign,
  Receipt,
  PieChart,
  ChevronDown,
  ChevronRight,
  GripVertical,
  MessageSquare,
  Check,
  X,
  Megaphone,
  TrendingUp,
  Upload,
  History,
  Target,
  Clock,
  Calendar,
  UserCheck,
  Flame,
  Layers,
  UserCog,
  Tag,
  ShieldAlert,
  BarChart2,
  Scale,
  Sparkles,
  CheckSquare,
  ShieldCheck,
  Pencil,
  Trash2,
  FileSearch,
  ArrowLeftRight,
  Percent, Globe,
  Wrench,

  Search,  Link2,} from 'lucide-react';

export default function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  // Branding for the footer line. Reads platform.tenants.name (the same
  // doc the login page reads) so the sidebar copyright tracks whatever
  // the tenant typed in Settings → Company Info. Defaults to "Your
  // Company" only when no tenant branding has been saved.
  const branding = useBranding();
  // Roles & Access — hides modules this person's role has switched off.
  // `can` fails open (returns true) while the grid loads and on any fetch
  // error, so the menu never collapses on a network blip.
  const { can } = usePermissions();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menuOrder, setMenuOrder] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempMenuOrder, setTempMenuOrder] = useState(null);
  // Red count badge on the Discount Approvals nav item — pending work is
  // visible without opening the bell. Polled lightly; staff-only (clients
  // never see the item). Failures are silent (best-effort visibility).
  const [discountPending, setDiscountPending] = useState(0);

  useEffect(() => {
    if (!user || user.role === 'client') return;
    let alive = true;
    const load = () => discountAPI.pendingApprovals()
      .then(({ data }) => { if (alive) setDiscountPending(data?.count || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.role]);

  // Unread inbound mail, and replies waiting to be filed. Both are work
  // awaiting THIS user — a client has written and nobody has read it — which
  // is the same bar the other nav badges are held to. Only meaningful once
  // the person has connected a mailbox; everyone else gets zero and no badge.
  const [mailUnread, setMailUnread] = useState(0);
  const [mailUnfiled, setMailUnfiled] = useState(0);
  useEffect(() => {
    if (!user || user.role === 'client') return;
    let alive = true;
    const load = async () => {
      try {
        const [unread, tray] = await Promise.all([
          emailAPI.getUnreadCount().catch(() => ({ data: {} })),
          emailAPI.getUnmatched().catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setMailUnread(unread.data?.unread_count || 0);
        setMailUnfiled(Array.isArray(tray.data) ? tray.data.length : 0);
      } catch { /* silent — best-effort visibility */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.role]);

  // Salesforce model: the notification BELL is the single home for
  // notifications — the left nav never mirrors unread-notification counts
  // (that's what made the Accounting tab read "9+" while only 1 item actually
  // needed action). Instead, nav badges are ACTIONABLE WORK QUEUES sourced from
  // real domain data: items awaiting THIS user's action. We badge the two
  // sections that have a genuine queue — Accounting (payment verifications) and
  // Sales › Discount Approvals — and leave everything else clean.
  const [pendingVerif, setPendingVerif] = useState(0);
  const canVerify = ['accountant', 'admin', 'super_admin'].includes(user?.role);
  useEffect(() => {
    if (!user || !canVerify) { setPendingVerif(0); return; }
    let alive = true;
    const load = async () => {
      try {
        const [reg, bal] = await Promise.all([
          accountingAPI.getPendingVerifications().catch(() => ({ data: [] })),
          salePaymentsAPI.listPending().catch(() => ({ data: { pending: [] } })),
        ]);
        const n = (Array.isArray(reg.data) ? reg.data.length : 0)
                + ((bal.data?.pending || []).length);
        if (alive) setPendingVerif(n);
      } catch { /* silent — best-effort visibility */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.role, canVerify]);

  // Open complaints badge — the user's original intent for the Complaints
  // surface: unresolved complaints must be impossible to miss (red count,
  // and the bell chime already fires on each new complaint). Role-scoped
  // server-side; officers see only their own cases' complaints.
  const [openComplaints, setOpenComplaints] = useState(0);
  const canSeeComplaints = ['super_admin', 'admin', 'process_manager', 'case_officer'].includes(user?.role);
  useEffect(() => {
    if (!user || !canSeeComplaints) { setOpenComplaints(0); return; }
    let alive = true;
    const load = () => complaintAPI.getOpenCount()
      .then(({ data }) => { if (alive) setOpenComplaints(data?.count || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.role, canSeeComplaints]);

  // Unassigned leads badge — a lead sitting in the pool is money nobody is
  // working. Website forms make that queue fill on its own, so the count has
  // to be visible from the nav without opening Marketing. Same rule as the
  // other badges: a real work queue, not a mirror of the bell.
  const [poolUnassigned, setPoolUnassigned] = useState(0);
  const canSeePool = ['super_admin', 'admin', 'marketing_manager'].includes(user?.role);
  useEffect(() => {
    if (!user || !canSeePool) { setPoolUnassigned(0); return; }
    let alive = true;
    const load = () => marketingAPI.getLeadPool({ limit: 1 })
      .then(({ data }) => { if (alive) setPoolUnassigned(data?.new_count ?? data?.total ?? 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.id, user?.role, canSeePool]);

  // path → count of items AWAITING ACTION in that section. Keyed by the exact
  // nav path so both the parent (via its own path) and the matching sub-item
  // pick it up. Accounting verifications live under both the admin and the
  // accountant accounting paths depending on role.
  const actionCounts = {
    '/admin/accounting': pendingVerif,
    '/accountant/accounting': pendingVerif,
    '/admin/discount/approvals': discountPending,
    '/admin/complaints': openComplaints,
    '/officer/complaints': openComplaints,
    '/admin/marketing/pool': poolUnassigned,
    // Keyed by the FULL path, query included: every Email sub-item shares
    // /admin/email and differs only by ?folder, so a base-path key would
    // badge Compose, Sent and Drafts at the same time.
    '/admin/email?folder=inbox': mailUnread,
    '/officer/email?folder=inbox': mailUnread,
    '/accountant/email?folder=inbox': mailUnread,
    '/sales/email?folder=inbox': mailUnread,
    '/admin/email?folder=unmatched': mailUnfiled,
    '/officer/email?folder=unmatched': mailUnfiled,
    '/accountant/email?folder=unmatched': mailUnfiled,
    '/sales/email?folder=unmatched': mailUnfiled,
  };

  // Total actionable items behind a nav item — its own path plus every submenu
  // item, de-duplicated (a parent path often equals its first child's, which
  // would otherwise double-count).
  // A path counts under its exact spelling when one is registered (so
  // ?folder=inbox and ?folder=unmatched are distinct queues), and under its
  // base path otherwise. The Set then prevents a parent double-counting a
  // child that shares its path.
  const countKey = (p) => (actionCounts[p] !== undefined ? p : p.split('?')[0]);

  const itemNotifCount = (item) => {
    const paths = new Set();
    if (item.path) paths.add(countKey(item.path));
    (item.submenu || []).forEach(s => { if (s.path) paths.add(countKey(s.path)); });
    let total = 0;
    paths.forEach(p => { total += actionCounts[p] || 0; });
    return total;
  };

  // Load saved menu order from localStorage
  useEffect(() => {
    if (user?.id) {
      const savedOrder = localStorage.getItem(`menuOrder_${user.id}`);
      if (savedOrder) {
        try {
          setMenuOrder(JSON.parse(savedOrder));
        } catch (e) {
          console.error('Failed to parse menu order');
        }
      }
    }
  }, [user?.id]);

  // Auto-expand active submenus. Runs over the vertical-applied items so the
  // expandedMenus keys match the labels the render pass uses (a renamed group
  // like Processing→"Deal Execution" must expand under its rendered label).
  useEffect(() => {
    const newExpanded = { ...expandedMenus };
    applyPermissionsToMenu(
      applyGrowthEngineGate(applyVerticalToMenu(getBaseMenuItems(), branding?.vertical), branding?.features?.growth_engine === true), can,
    ).forEach(item => {
      if (item.submenu) {
        const basePath = item.path.split('?')[0];
        const subPaths = item.submenu.map(s => s.path.split('?')[0]);
        const isActive = location.pathname.startsWith(basePath) ||
          subPaths.some(sp => location.pathname.startsWith(sp));
        if (isActive) {
          newExpanded[item.label] = true;
        }
      }
    });
    setExpandedMenus(newExpanded);
  }, [location.pathname]);

  const toggleSubmenu = (label, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const getBaseMenuItems = () => {
    switch (user?.role) {
      case 'super_admin':
        // Standard sequence (locked with user, 2026-08-01, from the Praxia
        // arrangement): identity first (Autopilot + AI Workforce under
        // Dashboard), then the funnel in customer-journey order (Marketing →
        // Sales → Processing → Accounting → Tasks), then org surfaces, then
        // governance. Per-user drag order (localStorage) still overrides.
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 2 },
          { icon: Sparkles, label: 'AI Workforce', path: '/ai-workforce', priority: 3, beta: true },
          {
            icon: Megaphone,
            label: 'Marketing',
            path: '/admin/marketing',
            priority: 4,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/marketing' },
              { icon: Upload, label: 'Import Leads', path: '/admin/marketing/upload' },
              { icon: History, label: 'Import History', path: '/admin/marketing/history' },
              { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool' },
              { icon: Sparkles, label: 'AI Pool', path: '/sales/lead-pool' },
              { icon: Globe, label: 'Web Forms', path: '/admin/marketing/web-forms' },
              { icon: Tag, label: 'Lead Sources', path: '/admin/marketing/sources' },
              { icon: BarChart3, label: 'Assigned Leads', path: '/admin/marketing/assigned-leads' },
              { icon: Megaphone, label: 'Meta Ads', path: '/admin/marketing/meta' },
              { icon: Compass, label: 'Acquisition', path: '/admin/marketing/acquisition' },
              { icon: Compass, label: 'Command Center', path: '/admin/marketing/growth' },
              { icon: Target, label: 'Growth Goal', path: '/admin/marketing/goal' },
              { icon: BrainCircuit, label: 'Growth Brain', path: '/admin/marketing/brain' },
              { icon: Wrench, label: 'Technical SEO', path: '/admin/marketing/technical-seo' },
              { icon: Search, label: 'Keyword Intelligence', path: '/admin/marketing/keywords' },
              { icon: Target, label: 'Competitor Intelligence', path: '/admin/marketing/competitors' },
              { icon: TrendingUp, label: 'Gap Intelligence', path: '/admin/marketing/gaps' },
              { icon: Link2, label: 'Authority Intelligence', path: '/admin/marketing/authority' },
              { icon: FileText, label: 'Content Intelligence', path: '/admin/marketing/content' },
              { icon: Sparkles, label: 'AI Visibility', path: '/admin/marketing/geo' },
              { icon: ShieldCheck, label: 'Brand Intelligence', path: '/admin/marketing/brand' },
              { icon: PenLine, label: 'Content Production', path: '/admin/marketing/content-production' },
            ]
          },
          {
            icon: TrendingUp,
            label: 'Sales',
            path: '/sales/manager',
            priority: 5,
            // Phase 1.9a.2 — discount + lead-governance items lifted in
            // from the top-level. They belong inside the Sales submenu
            // because they're sales-domain artefacts (records originate
            // in the pipeline). Marketing is top-of-funnel only.
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager' },
              { icon: Target, label: 'Pipeline', path: '/sales/manager/leads' },
              { icon: Sparkles, label: 'AI Pool', path: '/sales/lead-pool' },
              { icon: BarChart3, label: 'Reports', path: '/sales/reports' },
              // Renamed 2026-08-21: this is the cash-by-payment-month REPORT
              // (the incentive lens). Accounting → Collections is the work
              // queue. Two menu items with one name is how people end up on
              // the wrong page and conclude the data is missing.
              { icon: HandCoins, label: 'Collections Report', path: '/sales/collections' },
              { icon: ShieldCheck, label: 'Discount Approvals', path: '/admin/discount/approvals' },
              { icon: Percent, label: 'Discount Policy', path: '/admin/discount/policy' },
              { icon: ArrowLeftRight, label: 'Lead Reassignment Report', path: '/admin/leads/reassignment-report' },
              { icon: FileSearch, label: 'Lead Deletion Report', path: '/admin/leads/deletion-report' },
            ]
          },
          {
            icon: Layers,
            label: 'Processing',
            path: '/admin/cases',
            priority: 6,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/processing/overview' },
              { icon: Target, label: 'Pipeline', path: '/admin/pipeline' },
              { icon: Briefcase, label: 'Cases', path: '/admin/cases' },
              { icon: Users, label: 'Clients', path: '/admin/clients' },
              { icon: Building2, label: 'Visa Programs', path: '/admin/visa-programs' },
              { icon: ClipboardList, label: 'Checklists', path: '/admin/checklists' },
              { icon: Library, label: 'Document Library', path: '/admin/document-library' },
            ]
          },
          {
            icon: Calculator,
            label: 'Accounting',
            path: '/admin/accounting',
            priority: 7,
            submenu: [
              { icon: PieChart, label: 'Dashboard', path: '/admin/accounting' },
              { icon: MoneyIcon, label: 'Sales', path: '/admin/accounting/sales' },
              { icon: Receipt, label: 'Expenses', path: '/admin/accounting/expenses' },
              // Balance recovery moved off the consultant's follow-up queue on
              // 2026-08-21 — accounts owns it now, so it needs a home here.
              { icon: HandCoins, label: 'Collections', path: '/admin/accounting/collections' },
              { icon: BarChart3, label: 'Reports', path: '/admin/accounting/reports' },
            ]
          },
          { icon: CheckSquare,  label: 'Tasks',       path: '/tasks',       priority: 8 },
          // Complaints — a work queue, not a config page: red unresolved
          // count via actionCounts, right in the operational block.
          { icon: AlertCircle,  label: 'Complaints',  path: '/admin/complaints', priority: 9 },
          {
            icon: UserCog,
            label: 'HR',
            path: '/admin/users',
            priority: 10,
            submenu: [
              { icon: LayoutDashboard, label: 'HR Overview',  path: '/admin/hr/overview' },
              { icon: Users,           label: 'Staff',        path: '/admin/users' },
              { icon: Briefcase,       label: 'Job Applications', path: '/admin/hr/job-applications' },
              { icon: Clock,           label: 'Attendance',   path: '/admin/hr/attendance' },
              { icon: Calendar,        label: 'Leaves',       path: '/admin/hr/leaves' },
              { icon: MoneyIcon,      label: 'Payroll',      path: '/admin/hr/payroll' },
              { icon: Settings,        label: 'HR Policy',    path: '/admin/hr/policy' },
            ]
          },
          {
            icon: Mail,
            label: 'Email',
            path: '/admin/email',
            priority: 11,
            // Compose first — Gmail / Superhuman pattern. ?compose=true is
            // already wired in EmailModule.js to open the modal on mount.
            submenu: [
              { icon: Pencil, label: 'Compose', path: '/admin/email?compose=true' },
              { icon: Inbox, label: 'Inbox', path: '/admin/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/admin/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/admin/email?folder=drafts' },
              { icon: AtSign, label: 'My Email Account', path: '/admin/email?folder=account' },
              { icon: FileText, label: 'Templates', path: '/admin/email-templates' },
            ]
          },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', priority: 12 },
          {
            icon: Scale,
            label: 'Legal',
            path: '/legal',
            priority: 13,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/legal' },
              { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations' },
              { icon: BarChart2,       label: 'Analytics', path: '/legal/analytics' },
            ]
          },
          { icon: Handshake, label: 'B2B Partners', path: '/admin/b2b-partners', priority: 14 },
          // Complaint Types moved INSIDE Settings (user decision 2026-08-01)
          // — it's a taxonomy editor, not a daily surface. Complaint ALERTS
          // still arrive via the notification bell (+chime).
          { icon: ShieldCheck,  label: 'Compliance',  path: '/compliance',  priority: 15 },
          { icon: Trash2,       label: 'Recycle Bin', path: '/admin/recycle-bin', priority: 16 },
          { icon: Settings,     label: 'Settings',    path: '/admin/settings', priority: 17 },
        ];
      case 'admin':
        // Same standard sequence as super_admin (locked 2026-08-01) — see
        // the comment there. Admin differs only inside the Sales submenu
        // (no Lead Deletion Report, per feedback-lead-delete-admin-only).
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 2 },
          { icon: Sparkles, label: 'AI Workforce', path: '/ai-workforce', priority: 3, beta: true },
          {
            icon: Megaphone,
            label: 'Marketing',
            path: '/admin/marketing',
            priority: 4,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/marketing' },
              { icon: Upload, label: 'Import Leads', path: '/admin/marketing/upload' },
              { icon: History, label: 'Import History', path: '/admin/marketing/history' },
              { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool' },
              { icon: Sparkles, label: 'AI Pool', path: '/sales/lead-pool' },
              { icon: Globe, label: 'Web Forms', path: '/admin/marketing/web-forms' },
              { icon: Tag, label: 'Lead Sources', path: '/admin/marketing/sources' },
              { icon: BarChart3, label: 'Assigned Leads', path: '/admin/marketing/assigned-leads' },
              { icon: Megaphone, label: 'Meta Ads', path: '/admin/marketing/meta' },
              { icon: Compass, label: 'Acquisition', path: '/admin/marketing/acquisition' },
              { icon: Compass, label: 'Command Center', path: '/admin/marketing/growth' },
              { icon: Target, label: 'Growth Goal', path: '/admin/marketing/goal' },
              { icon: BrainCircuit, label: 'Growth Brain', path: '/admin/marketing/brain' },
              { icon: Wrench, label: 'Technical SEO', path: '/admin/marketing/technical-seo' },
              { icon: Search, label: 'Keyword Intelligence', path: '/admin/marketing/keywords' },
              { icon: Target, label: 'Competitor Intelligence', path: '/admin/marketing/competitors' },
              { icon: TrendingUp, label: 'Gap Intelligence', path: '/admin/marketing/gaps' },
              { icon: Link2, label: 'Authority Intelligence', path: '/admin/marketing/authority' },
              { icon: FileText, label: 'Content Intelligence', path: '/admin/marketing/content' },
              { icon: Sparkles, label: 'AI Visibility', path: '/admin/marketing/geo' },
              { icon: ShieldCheck, label: 'Brand Intelligence', path: '/admin/marketing/brand' },
              { icon: PenLine, label: 'Content Production', path: '/admin/marketing/content-production' },
            ]
          },
          {
            icon: TrendingUp,
            label: 'Sales',
            path: '/sales/manager',
            priority: 5,
            // Phase 1.9a.2 — see super_admin block above. Admin role
            // doesn't see Lead Deletion Report (super_admin-only per
            // feedback-lead-delete-admin-only).
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager' },
              { icon: Target, label: 'Pipeline', path: '/sales/manager/leads' },
              { icon: Sparkles, label: 'AI Pool', path: '/sales/lead-pool' },
              { icon: BarChart3, label: 'Reports', path: '/sales/reports' },
              // Renamed 2026-08-21: this is the cash-by-payment-month REPORT
              // (the incentive lens). Accounting → Collections is the work
              // queue. Two menu items with one name is how people end up on
              // the wrong page and conclude the data is missing.
              { icon: HandCoins, label: 'Collections Report', path: '/sales/collections' },
              { icon: ShieldCheck, label: 'Discount Approvals', path: '/admin/discount/approvals' },
              { icon: Percent, label: 'Discount Policy', path: '/admin/discount/policy' },
              { icon: ArrowLeftRight, label: 'Lead Reassignment Report', path: '/admin/leads/reassignment-report' },
            ]
          },
          {
            icon: Layers,
            label: 'Processing',
            path: '/admin/cases',
            priority: 6,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/processing/overview' },
              { icon: Target, label: 'Pipeline', path: '/admin/pipeline' },
              { icon: Briefcase, label: 'Cases', path: '/admin/cases' },
              { icon: Users, label: 'Clients', path: '/admin/clients' },
              { icon: Building2, label: 'Visa Programs', path: '/admin/visa-programs' },
              { icon: ClipboardList, label: 'Checklists', path: '/admin/checklists' },
              { icon: Library, label: 'Document Library', path: '/admin/document-library' },
            ]
          },
          {
            icon: Calculator,
            label: 'Accounting',
            path: '/admin/accounting',
            priority: 7,
            submenu: [
              { icon: PieChart, label: 'Dashboard', path: '/admin/accounting' },
              { icon: MoneyIcon, label: 'Sales', path: '/admin/accounting/sales' },
              { icon: Receipt, label: 'Expenses', path: '/admin/accounting/expenses' },
              // Balance recovery moved off the consultant's follow-up queue on
              // 2026-08-21 — accounts owns it now, so it needs a home here.
              { icon: HandCoins, label: 'Collections', path: '/admin/accounting/collections' },
              { icon: BarChart3, label: 'Reports', path: '/admin/accounting/reports' },
            ]
          },
          { icon: CheckSquare,  label: 'Tasks',       path: '/tasks',       priority: 8 },
          // Complaints — a work queue, not a config page: red unresolved
          // count via actionCounts, right in the operational block.
          { icon: AlertCircle,  label: 'Complaints',  path: '/admin/complaints', priority: 9 },
          {
            icon: UserCog,
            label: 'HR',
            path: '/admin/users',
            priority: 10,
            submenu: [
              { icon: LayoutDashboard, label: 'HR Overview',  path: '/admin/hr/overview' },
              { icon: Users,           label: 'Staff',        path: '/admin/users' },
              { icon: Briefcase,       label: 'Job Applications', path: '/admin/hr/job-applications' },
              { icon: Clock,           label: 'Attendance',   path: '/admin/hr/attendance' },
              { icon: Calendar,        label: 'Leaves',       path: '/admin/hr/leaves' },
              { icon: MoneyIcon,      label: 'Payroll',      path: '/admin/hr/payroll' },
              { icon: Settings,        label: 'HR Policy',    path: '/admin/hr/policy' },
            ]
          },
          {
            icon: Mail,
            label: 'Email',
            path: '/admin/email',
            priority: 11,
            // Compose first — Gmail / Superhuman pattern. ?compose=true is
            // already wired in EmailModule.js to open the modal on mount.
            submenu: [
              { icon: Pencil, label: 'Compose', path: '/admin/email?compose=true' },
              { icon: Inbox, label: 'Inbox', path: '/admin/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/admin/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/admin/email?folder=drafts' },
              { icon: AtSign, label: 'My Email Account', path: '/admin/email?folder=account' },
              { icon: FileText, label: 'Templates', path: '/admin/email-templates' },
            ]
          },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', priority: 12 },
          {
            icon: Scale,
            label: 'Legal',
            path: '/legal',
            priority: 13,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/legal' },
              { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations' },
              { icon: BarChart2,       label: 'Analytics', path: '/legal/analytics' },
            ]
          },
          { icon: Handshake, label: 'B2B Partners', path: '/admin/b2b-partners', priority: 14 },
          // Complaint Types moved INSIDE Settings (user decision 2026-08-01)
          // — it's a taxonomy editor, not a daily surface. Complaint ALERTS
          // still arrive via the notification bell (+chime).
          { icon: ShieldCheck,  label: 'Compliance',  path: '/compliance',  priority: 15 },
          { icon: Trash2,       label: 'Recycle Bin', path: '/admin/recycle-bin', priority: 16 },
          { icon: Settings,     label: 'Settings',    path: '/admin/settings', priority: 17 },
        ];
      case 'process_manager':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin', priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          {
            icon: Layers,
            label: 'Processing',
            path: '/admin/cases',
            priority: 2,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/processing/overview' },
              { icon: Target, label: 'Pipeline', path: '/admin/pipeline' },
              { icon: Briefcase, label: 'Cases', path: '/admin/cases' },
              { icon: Users, label: 'Clients', path: '/admin/clients' },
              { icon: Library, label: 'Document Library', path: '/admin/document-library' },
            ]
          },
          { icon: UserCog, label: 'My Team', path: '/manager/officers', priority: 3 },
          { icon: AlertCircle, label: 'Complaints', path: '/admin/complaints', priority: 3 },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics', priority: 4 },
          {
            icon: Megaphone,
            label: 'Marketing',
            path: '/admin/marketing',
            priority: 5,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/admin/marketing' },
              { icon: Upload, label: 'Import Leads', path: '/admin/marketing/upload' },
              { icon: History, label: 'Import History', path: '/admin/marketing/history' },
              // Lead Pool intentionally NOT shown to process_manager — they're
              // department-scoped to process work; the marketing inbox lives
              // with marketing/admin. Backend get_lead_pool 403s them too.
            ]
          },
          {
            icon: Scale,
            label: 'Legal',
            path: '/legal',
            priority: 6,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/legal' },
              { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations' },
            ]
          },
          { icon: CheckSquare,  label: 'Tasks',       path: '/tasks',       priority: 7 },
          { icon: ShieldCheck,  label: 'Compliance',  path: '/compliance',  priority: 8 },
          { icon: Sparkles,     label: 'AI Workforce', path: '/ai-workforce', priority: 9, beta: true },
        ];
      case 'case_officer':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/officer', priority: 1 },
          { icon: MessageSquare, label: 'Ask Euphoric', path: '/ask', priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          { icon: Target, label: 'Pipeline', path: '/officer/pipeline', priority: 2 },
          { icon: Briefcase, label: 'My Cases', path: '/officer/cases', priority: 2 },
          { icon: FolderOpen, label: 'Documents', path: '/officer/documents', priority: 3 },
          { icon: Library, label: 'Document Library', path: '/officer/document-library', priority: 4 },
          {
            icon: Mail,
            label: 'Email',
            path: '/officer/email',
            priority: 5,
            submenu: [
              { icon: Pencil, label: 'Compose', path: '/officer/email?compose=true' },
              { icon: Inbox, label: 'Inbox', path: '/officer/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/officer/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/officer/email?folder=drafts' },
              { icon: AtSign, label: 'My Email Account', path: '/officer/email?folder=account' },
            ]
          },
          { icon: AlertCircle, label: 'Complaints', path: '/officer/complaints', priority: 6 },
          { icon: CheckSquare, label: 'Tasks', path: '/tasks', priority: 7 },
        ];
      case 'accountant':
        return [
          { icon: MessageSquare, label: 'Ask Euphoric', path: '/ask', priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          // Accountant's "home" IS the accounting overview — both /accountant
          // and /accountant/accounting render the same AccountingDashboard
          // component, so a standalone "Dashboard" entry was a duplicate
          // doorway to the same room. Removed 2026-05-23 after smoke test
          // surfaced the redundancy. A future Phase 1.8.x can replace this
          // with a personalised "Accountant My Day" view (pending verifications
          // count, today's expenses to log, etc.) if we want the menu item back.
          {
            icon: Calculator,
            label: 'Accounting',
            path: '/accountant/accounting',
            priority: 1,
            submenu: [
              { icon: PieChart, label: 'Overview', path: '/accountant/accounting' },
              { icon: MoneyIcon, label: 'Sales', path: '/accountant/accounting/sales' },
              { icon: Receipt, label: 'Expenses', path: '/accountant/accounting/expenses' },
              { icon: HandCoins, label: 'Collections', path: '/accountant/accounting/collections' },
              { icon: BarChart3, label: 'Reports', path: '/accountant/accounting/reports' },
            ]
          },
          {
            icon: Mail,
            label: 'Email',
            path: '/accountant/email',
            priority: 3,
            submenu: [
              { icon: Pencil, label: 'Compose', path: '/accountant/email?compose=true' },
              { icon: Inbox, label: 'Inbox', path: '/accountant/email?folder=inbox' },
              { icon: Send, label: 'Sent', path: '/accountant/email?folder=sent' },
              { icon: FileEdit, label: 'Drafts', path: '/accountant/email?folder=drafts' },
              { icon: AtSign, label: 'My Email Account', path: '/accountant/email?folder=account' },
            ]
          },
        ];
      case 'client':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/client', priority: 1 },
          { icon: FileText, label: 'My Application', path: '/client/application', priority: 2 },
          { icon: FolderOpen, label: 'Documents', path: '/client/documents', priority: 3 },
          { icon: AlertCircle, label: 'Complaints', path: '/client/complaints', priority: 4 },
        ];
      case 'sales_consultant':
        // Attendance lives in the global TopHeader (AttendanceHeaderButton) —
        // no dedicated sidebar page, so we don't expose a broken link that
        // just re-renders the Dashboard component.
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/sales',           priority: 1 },
          { icon: MessageSquare,   label: 'Ask Euphoric', path: '/ask',          priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          { icon: Target,          label: 'My Leads',  path: '/sales/leads',     priority: 2 },
          { icon: Sparkles,        label: 'AI Pool',   path: '/sales/lead-pool', priority: 3, beta: true },
          { icon: BarChart3,       label: 'Reports',   path: '/sales/reports',   priority: 4 },
          { icon: HandCoins,       label: 'Collections', path: '/sales/collections', priority: 4 },
          { icon: Mail,            label: 'Email',     path: '/sales/email',     priority: 5 },
          { icon: Calendar,        label: 'Leaves',    path: '/admin/hr/leaves', priority: 6 },
          { icon: Clock,           label: 'My Attendance', path: '/admin/hr/my-attendance', priority: 7 },
        ];
      case 'sales_manager':
        // Manager nav stays minimal. Player-coach pattern (managers carry
        // their own leads in addition to running the team) is handled
        // inside the page via a Team ⇄ Mine toggle, not a separate sidebar
        // entry — flipping context many times a day shouldn't require a
        // route change. Same pattern HubSpot Sales Hub uses.
        return [
          { icon: LayoutDashboard, label: 'Overview',   path: '/sales/manager',              priority: 1 },
          { icon: MessageSquare,   label: 'Ask Euphoric', path: '/ask',                      priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          { icon: Target,          label: 'Pipeline',   path: '/sales/manager/leads',        priority: 2 },
          { icon: Sparkles,        label: 'AI Pool',    path: '/sales/lead-pool',            priority: 3, beta: true },
          { icon: UserCheck,       label: 'My Team',    path: '/sales/manager/leaderboard',  priority: 4 },
          { icon: BarChart3,       label: 'Reports',    path: '/sales/reports',              priority: 5 },
          { icon: HandCoins,       label: 'Collections', path: '/sales/collections',         priority: 5 },
          { icon: ArrowLeftRight,  label: 'Reassignments', path: '/admin/leads/reassignment-report', priority: 5 },
          { icon: ShieldCheck,     label: 'Discount Approvals', path: '/admin/discount/approvals', priority: 5 },
          { icon: Mail,            label: 'Email',      path: '/sales/email',                priority: 6 },
          { icon: Calendar,        label: 'Leaves',     path: '/admin/hr/leaves',            priority: 7 },
          { icon: Clock,           label: 'My Attendance', path: '/admin/hr/my-attendance',     priority: 8 },
        ];
      case 'marketing_manager':
        return [
          { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/marketing', priority: 1 },
          { icon: MessageSquare, label: 'Ask Euphoric', path: '/ask', priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          { icon: Upload, label: 'Upload Leads', path: '/admin/marketing/upload', priority: 2 },
          { icon: History, label: 'Import History', path: '/admin/marketing/history', priority: 3 },
          { icon: Users, label: 'Lead Pool', path: '/admin/marketing/pool', priority: 4 },
          { icon: Sparkles, label: 'AI Pool', path: '/sales/lead-pool', priority: 5, beta: true },
          { icon: TrendingUp, label: 'Analytics', path: '/admin/marketing/analytics', priority: 6 },
          { icon: BarChart3, label: 'Assigned Leads', path: '/admin/marketing/assigned-leads', priority: 7 },
          { icon: Globe, label: 'Web Forms', path: '/admin/marketing/web-forms', priority: 7.5 },
          { icon: Tag, label: 'Lead Sources', path: '/admin/marketing/sources', priority: 8 },
          {
            icon: Target,
            label: 'Sales Insights',
            path: '/sales/manager',
            priority: 6,
            submenu: [
              { icon: LayoutDashboard, label: 'Overview', path: '/sales/manager' },
              { icon: Target, label: 'Pipeline', path: '/sales/manager/leads' },
              { icon: UserCheck, label: 'Leaderboard', path: '/sales/manager/leaderboard' },
              { icon: BarChart3, label: 'Reports', path: '/sales/reports' },
            ]
          },
        ];
      case 'legal_counsel':
        return [
          { icon: LayoutDashboard, label: 'Dashboard',   path: '/legal',                    priority: 1 },
          { icon: MessageSquare,   label: 'Ask Euphoric', path: '/ask',                     priority: 1 },
          { icon: Sparkles, label: 'Autopilot', path: '/admin/autopilot', priority: 1 },
          { icon: ShieldAlert,     label: 'Escalations', path: '/legal/escalations',         priority: 2 },
          { icon: BarChart2,       label: 'Analytics',   path: '/legal/analytics',           priority: 3 },
        ];
      default:
        return [];
    }
  };

  // Apply saved order or use default. Vertical Editions filter/rename runs
  // FIRST so hidden modules never render and saved menu-order keys align
  // with the labels the user actually sees. applyVerticalToMenu returns the
  // array untouched when the tenant has no vertical (immigration default).
  const getMenuItems = () => {
    // Vertical filter first (what this Edition offers at all), then the
    // Roles & Access filter (what THIS person may reach of it).
    const baseItems = applyPermissionsToMenu(
      applyGrowthEngineGate(applyVerticalToMenu(getBaseMenuItems(), branding?.vertical), branding?.features?.growth_engine === true),
      can,
    );
    if (menuOrder && user?.role !== 'client') {
      // Sort by saved order
      return [...baseItems].sort((a, b) => {
        const orderA = menuOrder[a.label] ?? a.priority;
        const orderB = menuOrder[b.label] ?? b.priority;
        return orderA - orderB;
      });
    }
    return baseItems;
  };

  // Drag and drop handlers with visual feedback
  const [dragOverItem, setDragOverItem] = useState(null);
  
  // Edit mode functions
  const handleEnterEditMode = () => {
    setTempMenuOrder(menuOrder ? { ...menuOrder } : null);
    setIsEditMode(true);
  };

  const handleSaveOrder = () => {
    // tempMenuOrder already has the latest order from drag operations
    if (tempMenuOrder) {
      setMenuOrder(tempMenuOrder);
      localStorage.setItem(`menuOrder_${user.id}`, JSON.stringify(tempMenuOrder));
    }
    setIsEditMode(false);
    setTempMenuOrder(null);
  };

  const handleCancelEdit = () => {
    setTempMenuOrder(null);
    setIsEditMode(false);
    setDraggedItem(null);
    setDragOverItem(null);
  };
  
  const handleDragStart = (e, item) => {
    if (!isEditMode) return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, item) => {
    e.preventDefault();
    if (!isEditMode || !draggedItem || draggedItem.label === item.label) return;
    setDragOverItem(item.label);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    setDragOverItem(null);
    if (!isEditMode || !draggedItem || draggedItem.label === targetItem.label) return;

    const items = getMenuItems();
    const dragIndex = items.findIndex(i => i.label === draggedItem.label);
    const targetIndex = items.findIndex(i => i.label === targetItem.label);

    // Create new order
    const newOrder = {};
    const reorderedItems = [...items];
    reorderedItems.splice(dragIndex, 1);
    reorderedItems.splice(targetIndex, 0, draggedItem);
    
    reorderedItems.forEach((item, index) => {
      newOrder[item.label] = index + 1;
    });

    // Store in temp order (will be saved when clicking Save)
    setTempMenuOrder(newOrder);
    setMenuOrder(newOrder);  // Update display immediately
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const menuItems = getMenuItems();
  const canEditMenu = user?.role !== 'client';

  return (
    <>
    {/* Global fixed top header — appears on every page */}
    <div
      className="fixed top-0 right-0 z-40 flex items-center justify-end gap-2 px-5 bg-white border-b border-slate-200"
      style={{ left: '260px', height: '56px' }}
      data-testid="global-top-header"
    >
      {user?.role !== 'client' && <AttendanceHeaderButton />}
      {/* AvailabilityToggle for roles that can claim AI leads */}
      {['sales_consultant', 'sales_manager', 'admin', 'super_admin'].includes(user?.role) && (
        <>
          <div className="w-px h-6 bg-slate-200" />
          <AvailabilityToggle />
        </>
      )}
      <div className="w-px h-6 bg-slate-200" />
      <LanguageSelector />
      <NotificationBell />
      <ProfileMenu />
    </div>

    <div className="sidebar group/sidebar" data-testid="sidebar">
      {/* Fixed 208×64 container so every tenant's logo (whatever its
          aspect ratio) sits in the same visual footprint. max-w-full +
          max-h-full + object-contain inside TenantLogo means a tight
          wordmark and a padded icon-text logo both fit proportionally.
          User feedback 2026-06-02: w-44 h-12 read as too small in the
          rail; bumped to w-52 h-16 for stronger brand presence. */}
      <div className="p-4 border-b border-slate-200 flex items-center justify-center">
        <div className="w-52 h-16 flex items-center justify-center">
          <TenantLogo className="max-w-full max-h-full object-contain" textClassName="text-lg font-bold truncate" data-testid="sidebar-logo" />
        </div>
      </div>

      {/* Edit Mode Banner - Only shows when in edit mode */}
      {isEditMode && (
        <div className="px-3 py-2 bg-teal-50 border-b border-teal-200 flex items-center justify-between">
          <span className="text-xs font-medium text-teal-700">Drag to reorder</span>
          <div className="flex items-center gap-1">
            <button
              onClick={handleSaveOrder}
              className="p-1.5 rounded-md bg-teal-500 hover:bg-teal-600 text-white transition-colors"
              title="Save"
              data-testid="save-menu-order"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleCancelEdit}
              className="p-1.5 rounded-md bg-white hover:bg-slate-100 text-slate-600 transition-colors border border-slate-200"
              title="Cancel"
              data-testid="cancel-edit-menu"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="p-3 flex-1 overflow-y-auto">
        <nav className="space-y-1" data-testid="sidebar-nav">
          {(() => {
            // Longest-prefix active-state detection. Without this, on
            // /sales/reports both Dashboard (/sales) and Reports
            // (/sales/reports) light up because /sales/reports startsWith
            // /sales/. We pick the single most-specific match here and use
            // it for the per-item isActive check below.
            const allBasePaths = menuItems.map(i => i.path.split('?')[0]);
            const longestActiveBasePath = allBasePaths
              .filter(p => location.pathname === p || location.pathname.startsWith(p + '/'))
              .reduce((longest, p) => (p.length > longest.length ? p : longest), '');

            return menuItems.map((item) => {
              const Icon = item.icon;

              const basePath = item.path.split('?')[0];
              // Pure longest-prefix wins — same rule for every item including
              // dashboards. At /sales/reports, /sales/reports beats /sales so
              // only Reports highlights. At an unknown sub-route like
              // /admin/some-page that no item matches exactly, /admin still
              // wins as the closest ancestor and Dashboard stays highlighted
              // (Salesforce / HubSpot behaviour). Verified by a 20-case truth
              // table covering admin, manager, and consultant menus.
              const isActive = basePath === longestActiveBasePath;
            
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedMenus[item.label];
            
            return (
              <div 
                key={item.path}
                draggable={isEditMode}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={(e) => handleDragOver(e, item)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item)}
                onDragEnd={handleDragEnd}
                className={`
                  transition-all duration-200 rounded-lg
                  ${draggedItem?.label === item.label ? 'opacity-50 scale-95 bg-slate-100' : ''}
                  ${dragOverItem === item.label && isEditMode ? 'border-t-2 border-teal-500 bg-teal-50' : 'border-t-2 border-transparent'}
                `}
              >
                <div className="flex items-center">
                  {isEditMode && (
                    <div className="cursor-grab active:cursor-grabbing pr-1 text-teal-500">
                      <GripVertical size={14} />
                    </div>
                  )}
                  <Link
                    to={hasSubmenu ? '#' : item.path}
                    onClick={(e) => {
                      if (hasSubmenu) {
                        toggleSubmenu(item.label, e);
                      }
                    }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex-1 flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} className={isActive ? 'text-white' : 'text-slate-600'} />
                      <span className="font-medium">{item.label}</span>
                      {itemNotifCount(item) > 0 && (
                        <span
                          className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                          title={`${itemNotifCount(item)} item${itemNotifCount(item) !== 1 ? 's' : ''} awaiting action in this section`}
                        >
                          {itemNotifCount(item)}
                        </span>
                      )}
                      {item.beta && (
                        <span
                          className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200"
                          title="This feature is in Beta — functionality may evolve"
                        >
                          Beta
                        </span>
                      )}
                    </div>
                    {hasSubmenu && (
                      <div 
                        onClick={(e) => toggleSubmenu(item.label, e)}
                        className={`p-1 rounded hover:bg-white/10 transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                      >
                        <ChevronDown size={16} className={isActive ? 'text-white' : 'text-slate-500'} />
                      </div>
                    )}
                  </Link>
                </div>
                
                {/* Submenu items with smooth animation. The expanded cap only
                    drives the open/close transition — it must stay above the
                    tallest submenu (Marketing is 10 items ≈ 400px; max-h-96 = 384px
                    clipped Acquisition, found on staging 2026-09-04). */}
                {hasSubmenu && (
                  <div
                    className={`ml-8 mt-1 space-y-1 overflow-hidden transition-all duration-200 ${
                      isExpanded ? 'max-h-[60rem] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    {item.submenu.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const subBasePath = subItem.path.split('?')[0];
                      // When submenu items share a base path (Email's
                      // Compose/Inbox/Sent/Drafts all map to /admin/email),
                      // we need the query string to disambiguate. Old logic
                      // used `||` which lit up every sibling at once.
                      const subQuery = subItem.path.includes('?') ? subItem.path.split('?')[1] : null;
                      const isSubActive = subQuery
                        ? (location.pathname === subBasePath && location.search.includes(subQuery))
                        : location.pathname === subBasePath;
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                            isSubActive
                              ? 'bg-slate-800 text-white'
                              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                          }`}
                        >
                          <SubIcon size={16} />
                          <span>{subItem.label}</span>
                          {(actionCounts[countKey(subItem.path)] || 0) > 0 && (
                            <span
                              className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                              title={`${actionCounts[countKey(subItem.path)]} item${actionCounts[countKey(subItem.path)] !== 1 ? 's' : ''} awaiting action`}
                            >
                              {actionCounts[countKey(subItem.path)]}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          });
          })()}
        </nav>
      </div>

      {/* Footer with copyright and hover-reveal customize button */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center justify-center">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} {branding?.name || 'Your Company'}
          </p>
          {canEditMenu && !isEditMode && (
            <button
              onClick={handleEnterEditMode}
              className="ml-2 p-1 rounded-md text-slate-300 hover:text-slate-500 hover:bg-slate-100 opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200"
              title="Customize menu order"
              data-testid="edit-menu-order"
            >
              <Settings size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
